import { FC } from 'react';
import { useSubscribeToInvalidationState } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import CategoryIcon from '@mui/icons-material/Category';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';
import Row from 'components/CategoryListRow';
import Add from 'components/CategoryListAdd';
import Sorting, { useSorting } from 'components/Sorting';

import { useAvoidSuspense, useDelete } from 'lib';

import Query, {
  CategoryListRefetchQuery,
} from '__generated__/CategoryListRefetchQuery.graphql';
import { CategoryList_data$key } from '__generated__/CategoryList_data.graphql';

type Props = {
  data: CategoryList_data$key;
};

const CategoryList: FC<Props> = ({ data }) => {
  const [{ categories }, { refetch, loading }] = useAvoidSuspense<
    CategoryListRefetchQuery,
    CategoryList_data$key
  >(
    graphql`
      fragment CategoryList_data on Query
      @refetchable(queryName: "CategoryListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 2147483647 }
        after: { type: "String" }
      ) {
        categories(first: $first, after: $after) {
          __id
          edges {
            node {
              id
              ...CategoryListRow_category
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    data,
    Query
  );

  const connectionID = categories?.__id;

  const [deleteCategory, deleting] = useDelete(connectionID);

  const [sort, sorting] = useSorting(
    graphql`
      mutation CategoryListSortMutation(
        $positions: [EntityWithPositionInput!]!
      ) {
        updateCategoryPositions(positions: $positions)
      }
    `,
    connectionID
  );

  const ids = connectionID ? [connectionID] : [];
  useSubscribeToInvalidationState(ids, () => {
    refetch({});
  });

  const { edges, pageInfo } = categories ?? {};

  if (!edges) return null;

  return (
    <Box>
      <Sorting edges={edges} onSort={sort}>
        <Table
          pageInfo={pageInfo ?? null}
          loading={loading || sorting || deleting}
          icon={<CategoryIcon />}
          head={
            <TableRow>
              <TableCell sx={{ width: '20px' }} />
              <TableCell>Pavadimas</TableCell>
              <TableCell />
            </TableRow>
          }>
          {edges?.map(({ node }) => (
            <Row
              sortDisabled={sorting}
              category={node}
              key={node.id}
              onDelete={deleteCategory}
            />
          ))}
        </Table>
      </Sorting>

      {connectionID && (
        <FabDialog fullWidth maxWidth="sm">
          <Add connectionID={connectionID} />
        </FabDialog>
      )}
    </Box>
  );
};

export default CategoryList;
